import type {
  ExamSectionDraft,
  ExamSectionId,
  HireHealthFormValues,
  SectionSnapshot,
  TextSectionDraft,
  TextSectionId,
} from './hireHealthSurveyTypes';
import { HIRE_HEALTH_DETAIL_MAX_CHARS } from './hireHealthSurveyTextLimits';

const EXAM_PREFIX: Record<ExamSectionId, { hcd: keyof HireHealthFormValues; inn: keyof HireHealthFormValues; dj: keyof HireHealthFormValues }> = {
  general: { hcd: 'hcdGeneral', inn: 'inGeneral', dj: 'djGeneral' },
  pneumoconiosis: { hcd: 'hcdPneumoconiosis', inn: 'inPneumoconiosis', dj: 'djPneumoconiosis' },
  vibration: { hcd: 'hcdVibration', inn: 'inVibration', dj: 'djVibration' },
  hearing: { hcd: 'hcdHearingLoss', inn: 'inHearingLoss', dj: 'djHearingLoss' },
  specialized: { hcd: 'hcdSpecialized', inn: 'inSpecialized', dj: 'djSpecialized' },
};

export function emptyExamDraft(): ExamSectionDraft {
  return { hadExam: '', examDate: '', hospital: '', fit: '' };
}

export function emptyTextDraft(): TextSectionDraft {
  return { hadIssue: '', detail: '' };
}

function capDetail(text: string): string {
  return text.trim().slice(0, HIRE_HEALTH_DETAIL_MAX_CHARS);
}

export function examDraftFromValues(sectionId: ExamSectionId, values: HireHealthFormValues): ExamSectionDraft {
  const p = EXAM_PREFIX[sectionId];
  const hcd = values[p.hcd];
  const inn = values[p.inn];
  const dj = values[p.dj];
  const hadExam =
    hcd || inn || dj
      ? 'yes'
      : '';
  let fit: ExamSectionDraft['fit'] = '';
  if (dj === 'true') fit = 'fit';
  if (dj === 'false') fit = 'not_fit';
  return {
    hadExam,
    examDate: hcd ?? '',
    hospital: inn ?? '',
    fit,
  };
}

export function textDraftFromValues(sectionId: TextSectionId, values: HireHealthFormValues): TextSectionDraft {
  const raw = sectionId === 'aftereffects' ? values.aftereffects : values.symptoms;
  if (!raw) {
    return { hadIssue: '', detail: '' };
  }
  return { hadIssue: 'yes', detail: raw };
}

export function mergeValuesFromSnapshots(
  exams: Partial<Record<ExamSectionId, ExamSectionDraft>>,
  texts: Partial<Record<TextSectionId, TextSectionDraft>>,
  dateConfirmed?: string
): HireHealthFormValues {
  const out: HireHealthFormValues = {};
  (Object.keys(EXAM_PREFIX) as ExamSectionId[]).forEach((sid) => {
    const d = exams[sid];
    if (!d) return;
    const p = EXAM_PREFIX[sid];
    if (d.hadExam === 'yes') {
      if (d.examDate) out[p.hcd] = d.examDate;
      if (d.hospital.trim()) out[p.inn] = d.hospital.trim();
      if (d.fit === 'fit') out[p.dj] = 'true';
      if (d.fit === 'not_fit') out[p.dj] = 'false';
    }
  });
  if (texts.aftereffects?.hadIssue === 'yes' && texts.aftereffects.detail.trim()) {
    out.aftereffects = capDetail(texts.aftereffects.detail);
  }
  if (texts.symptoms?.hadIssue === 'yes' && texts.symptoms.detail.trim()) {
    out.symptoms = capDetail(texts.symptoms.detail);
  }
  if (dateConfirmed) {
    out.dateConfirmed = dateConfirmed;
  }
  return out;
}

export function snapshotFromExam(sectionId: ExamSectionId, data: ExamSectionDraft): SectionSnapshot {
  return { kind: 'exam', sectionId, data: { ...data } };
}

export function snapshotFromText(sectionId: TextSectionId, data: TextSectionDraft): SectionSnapshot {
  return { kind: 'text', sectionId, data: { ...data } };
}
